import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SUPPORT_LEVELS, SUPPORT_COLORS, OUTREACH_OUTCOMES } from './shared/constants';
import { calculateAge } from './shared/utils';
import { Icon } from './shared/Icon';
import { ProgressRing, SegmentDonut } from './shared/Charts';

const countBy = (arr, prop) =>
  arr.reduce((acc, item) => {
    const key = item[prop] || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

// Never rounds to 0% or 100% unless truly 0 or 100
const fmtPct = (raw) => {
  if (raw === 0)   return '0%';
  if (raw === 100) return '100%';
  for (const dp of [0, 1, 2, 3]) {
    const s = raw.toFixed(dp);
    const n = parseFloat(s);
    if (n > 0 && n < 100) return `${s}%`;
  }
  return `${raw.toFixed(2)}%`;
};

const rp = (n, base) => base > 0 ? (n / base * 100) : 0;

// Minimum 0.4% bar width so even 1 voter shows a sliver
const Bar = ({ n, base, color = '#3b82f6', height = 8 }) => {
  const raw = rp(n, base);
  const w   = n > 0 ? Math.max(raw, 0.4) : 0;
  return (
    <div style={{ height, background: 'var(--surface-2)', borderRadius: 4 }}>
      <div style={{ height, width: `${Math.min(w, 100)}%`, background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
    </div>
  );
};

const card = { background: 'var(--surface)', borderRadius: '14px', padding: '20px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' };
const TABS = ['Overview', 'Outreach', 'Geography', 'Demographics', 'Trends'];
const AGE_BANDS = [
  { label: '18–29', min: 18, max: 29 },
  { label: '30–44', min: 30, max: 44 },
  { label: '45–59', min: 45, max: 59 },
  { label: '60+',   min: 60, max: 200 },
];
const voterAge = (v) => (v.age != null && v.age !== '' ? Number(v.age) : calculateAge(v.dob));
// Monday-anchored start of the week containing d, at local midnight.
const weekStart = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };

export default function Analytics({ supabase, allVoters = [], profile, user }) {
  profile = profile ?? user;
  const navigate = useNavigate();

  const [tab, setTab]                   = useState('Overview');
  const [drillDown, setDrillDown]       = useState(null); // null | 'all' | 'supporters' | 'undecided' | 'against' | 'committed' | 'contacted' | dynamic prefix keys
  const drillDownRef                    = useRef(null);
  useEffect(() => {
    if (drillDown && drillDownRef.current) {
      drillDownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [drillDown]);
  const [committedIds, setCommittedIds] = useState(new Set());
  const [outreach, setOutreach]         = useState([]);
  const [supportHistory, setSupportHistoryAll] = useState([]);
  const [reminderCounts, setReminderCounts] = useState({ upcoming: 0, overdue: 0, today: 0 });
  const [upcomingTripsCount, setUpcomingTripsCount] = useState(0);
  const [eventCounts, setEventCounts]   = useState({ total: 0, upcoming: 0 });
  // Full record lists for drill-down panels (small)
  const [upcomingEvents, setUpcomingEvents]       = useState([]);
  const [upcomingTrips, setUpcomingTrips]         = useState([]);
  const [todayReminders, setTodayReminders]       = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [expandedIslands, setExpandedIslands] = useState(new Set());
  const [opponents, setOpponents] = useState(() => {
    try { return JSON.parse(localStorage.getItem('crm_opponents') || '[]').map(o => typeof o === 'string' ? { name: o } : o); } catch { return []; }
  });
  const [editingOpponents, setEditingOpponents] = useState(false);
  const [opponentDraft, setOpponentDraft] = useState([]);
  const [supportView, setSupportView] = useState('levels'); // 'levels' | 'competitor'

  const toggleIsland = (island) => {
    setExpandedIslands(prev => {
      const next = new Set(prev);
      if (next.has(island)) next.delete(island); else next.add(island);
      return next;
    });
  };

  const openOpponentEditor = () => {
    setOpponentDraft(opponents.map(o => ({ name: o.name })));
    setEditingOpponents(true);
  };

  const saveOpponents = async () => {
    const cleaned = opponentDraft.filter(o => o.name.trim()).map(o => ({ name: o.name.trim() }));
    setOpponents(cleaned);
    localStorage.setItem('crm_opponents', JSON.stringify(cleaned));
    await Promise.all([
      supabase.storage.from('photos').upload(
        'config/opponents.json',
        JSON.stringify(cleaned),
        { contentType: 'application/json', upsert: true }
      ),
      supabase.from('app_settings').upsert({ key: 'opponents', value: cleaned }),
    ]);
    setEditingOpponents(false);
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    supabase.from('app_settings').select('value').eq('key', 'opponents').single()
      .then(({ data, error }) => {
        // PGRST116 = no row yet (fresh install) — expected, not an error.
        if (error && error.code !== 'PGRST116') console.error('[analytics] opponents load failed:', error);
        if (data?.value && Array.isArray(data.value) && data.value.length > 0) {
          setOpponents(data.value.map(o => typeof o === 'string' ? { name: o } : o));
        } else {
          // Backfill app_settings from storage for installs that saved before dual-write was added.
          supabase.storage.from('photos').download('config/opponents.json')
            .then(({ data: blob }) => {
              if (!blob) return;
              blob.text().then(text => {
                try {
                  const parsed = JSON.parse(text).map(o => typeof o === 'string' ? { name: o } : o).filter(o => o.name);
                  if (parsed.length > 0) {
                    setOpponents(parsed);
                    supabase.from('app_settings').upsert({ key: 'opponents', value: parsed });
                  }
                } catch {}
              });
            });
        }
      });

    supabase.from('outreach')
      .select('voter_id, outcome, type, date, created_at, logged_by, logged_by_profile:profiles(full_name)')
      .then(({ data, error }) => {
        if (error) console.error('[analytics] outreach load failed:', error);
        setOutreach(data || []);
      });

    // Support history — used to derive currently-withdrawn voters
    supabase.from('support_history')
      .select('voter_id, changed_from, changed_to, changed_at')
      .order('changed_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('[analytics] support_history load failed:', error);
        setSupportHistoryAll(data || []);
      });

    // Fetch all commitment-related outreach (made & withdrawn) ordered newest first.
    // A voter is currently committed only if their LATEST commitment-related record is 'Commitment Made'.
    supabase.from('outreach').select('voter_id, outcome, created_at')
      .in('outcome', ['Commitment Made', 'Commitment Withdrawn'])
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('[analytics] commitments load failed:', error);
        const seen = new Set();
        const committed = new Set();
        for (const row of (data || [])) {
          if (seen.has(row.voter_id)) continue;
          seen.add(row.voter_id);
          if (row.outcome === 'Commitment Made') committed.add(row.voter_id);
        }
        setCommittedIds(committed);
      });

    Promise.all([
      supabase.from('reminders').select('*').eq('completed', false).gt('reminder_date', today).order('reminder_date', { ascending: true }).limit(50),
      supabase.from('reminders').select('*', { count: 'exact', head: true }).eq('completed', false).lt('reminder_date', today),
      supabase.from('reminders').select('*').eq('completed', false).eq('reminder_date', today).order('reminder_time', { ascending: true }).limit(50),
    ]).then(([up, ov, td]) => {
      if (up.error) console.error('[analytics] upcoming reminders load failed:', up.error);
      if (ov.error) console.error('[analytics] overdue reminders count failed:', ov.error);
      if (td.error) console.error('[analytics] today reminders load failed:', td.error);
      setReminderCounts({ upcoming: up.data?.length || 0, overdue: ov.count || 0, today: td.data?.length || 0 });
      setUpcomingReminders(up.data || []);
      setTodayReminders(td.data || []);
    });

    supabase.from('campaign_trips').select('*')
      .gte('start_date', today).neq('status', 'cancelled').neq('status', 'completed')
      .order('start_date', { ascending: true }).limit(50)
      .then(({ data, error }) => {
        if (error) console.error('[analytics] campaign_trips load failed:', error);
        setUpcomingTrips(data || []);
        setUpcomingTripsCount(data?.length || 0);
      });

    Promise.all([
      supabase.from('events').select('*', { count: 'exact', head: true }).neq('completed', true),
      supabase.from('events').select('*').gte('date', today).neq('completed', true).order('date', { ascending: true }).limit(50),
    ]).then(([all, up]) => {
      setEventCounts({ total: all.count || 0, upcoming: up.data?.length || 0 });
      setUpcomingEvents(up.data || []);
    });
  }, [supabase]);

  // ── Voter stats ──────────────────────────────────────────
  const total          = allVoters.length;
  const supportCounts  = countBy(allVoters, 'support');
  const strongSupport  = supportCounts['Strong Support']  || 0;
  const leaningSupport = supportCounts['Leaning Support'] || 0;
  const leaningAgainst = supportCounts['Leaning Against'] || 0;
  const strongAgainst  = supportCounts['Strong Against']  || 0;
  const undecided      = supportCounts['Undecided']       || 0;
  const totalSupport   = strongSupport + leaningSupport;
  const totalAgainst   = strongAgainst + leaningAgainst;
  const commitmentYes  = allVoters.filter(v => committedIds.has(v.id)).length;
  const needed         = Math.ceil(total / 2);
  const toWin          = needed - totalSupport;
  const supportSegments = [
    { label: 'Strong Support',  value: strongSupport,  color: 'var(--sup-strong-dot)' },
    { label: 'Leaning Support', value: leaningSupport, color: 'var(--sup-lean-dot)' },
    { label: 'Undecided',       value: undecided,      color: 'var(--text-3)' },
    { label: 'Leaning Against', value: leaningAgainst, color: 'var(--sup-against-lean-dot)' },
    { label: 'Strong Against',  value: strongAgainst,  color: 'var(--sup-against-dot)' },
  ];

  // Opponent vote counts derived from voter.preferred_candidate field
  const againstVoters        = allVoters.filter(v => v.support === 'Leaning Against' || v.support === 'Strong Against');
  const preferredCandidateCounts = countBy(againstVoters, 'preferred_candidate');
  const opponentStats = opponents.map(o => ({
    name: o.name,
    votes: preferredCandidateCounts[o.name] || 0,
  }));
  const unassignedAgainst = totalAgainst - opponentStats.reduce((s, o) => s + o.votes, 0);

  // ── Outreach stats ───────────────────────────────────────
  const outreachTotal   = outreach.length;
  const votersContacted = new Set(outreach.map(o => o.voter_id)).size;
  // Match the Outreach page's logic exactly:
  // - Commitments = voters with a current 'Commitment Made' outreach record
  // - Withdrawals = voters whose latest support_history change was supporter→non-supporter
  //                 AND who don't currently have a Commitment Made record
  const SUPPORTER_LEVELS = ['Strong Support', 'Leaning Support'];
  const committedVoterIds = (() => {
    const s = new Set();
    for (const o of outreach) if (o.outcome === 'Commitment Made') s.add(o.voter_id);
    return s;
  })();
  const withdrawnVoterIds = (() => {
    const latestPerVoter = new Map();
    for (const h of supportHistory) {
      if (!latestPerVoter.has(h.voter_id)) latestPerVoter.set(h.voter_id, h);
    }
    const ids = new Set();
    for (const [vid, h] of latestPerVoter) {
      const wasSupporter = SUPPORTER_LEVELS.includes(h.changed_from);
      const isSupporter  = SUPPORTER_LEVELS.includes(h.changed_to);
      if (wasSupporter && !isSupporter && !committedVoterIds.has(vid)) ids.add(vid);
    }
    return ids;
  })();
  const outcomeCounts = (() => {
    const raw = countBy(outreach, 'outcome');
    raw['Commitment Made'] = committedVoterIds.size;
    raw['Commitment Withdrawn'] = withdrawnVoterIds.size;
    return raw;
  })();
  // Exclude internal/auto-generated types from Contact Methods chart.
  const typeCounts = (() => {
    const raw = countBy(outreach, 'type');
    delete raw['System'];
    delete raw['Support Level Change'];
    return raw;
  })();
  const teamMap = {};
  outreach.forEach(o => {
    const name = o.logged_by_profile?.full_name || 'Unknown';
    teamMap[name] = (teamMap[name] || 0) + 1;
  });
  const teamStats = Object.entries(teamMap).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const genderCounts = countBy(allVoters, 'gender');
  const islandCounts = countBy(allVoters, 'island');

  // ── Demographics: age bands ──────────────────────────────
  const ageCounts = (() => {
    const c = {}; AGE_BANDS.forEach(b => { c[b.label] = 0; }); c['Unknown'] = 0;
    for (const v of allVoters) {
      const a = voterAge(v);
      if (a == null || isNaN(a)) { c['Unknown']++; continue; }
      const band = AGE_BANDS.find(b => a >= b.min && a <= b.max);
      c[band ? band.label : 'Unknown']++;
    }
    return c;
  })();
  const ageKnown = Object.entries(ageCounts).reduce((s, [k, n]) => k === 'Unknown' ? s : s + n, 0);

  // ── Trends: last 8 weeks (net supporter change + outreach volume) ──
  const TREND_WEEKS = 8;
  const weeks = (() => {
    const tw = weekStart(new Date()); const arr = [];
    for (let i = TREND_WEEKS - 1; i >= 0; i--) { const d = new Date(tw); d.setDate(d.getDate() - i * 7); arr.push(d); }
    return arr;
  })();
  const weekIdx = new Map(weeks.map((d, i) => [d.getTime(), i]));
  const netByWeek = new Array(TREND_WEEKS).fill(0);
  for (const h of supportHistory) {
    if (!h.changed_at) continue;
    const i = weekIdx.get(weekStart(h.changed_at).getTime());
    if (i == null) continue;
    const was = SUPPORTER_LEVELS.includes(h.changed_from);
    const is  = SUPPORTER_LEVELS.includes(h.changed_to);
    if (is && !was) netByWeek[i] += 1; else if (was && !is) netByWeek[i] -= 1;
  }
  const volByWeek = new Array(TREND_WEEKS).fill(0);
  for (const o of outreach) {
    const dt = o.date || o.created_at; if (!dt) continue;
    const i = weekIdx.get(weekStart(dt).getTime());
    if (i != null) volByWeek[i] += 1;
  }
  const netTotal = netByWeek.reduce((a, b) => a + b, 0);
  const volTotal = volByWeek.reduce((a, b) => a + b, 0);
  const weekLabel = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  // ── Shareable report — opens a clean printable one-pager ──
  const downloadReport = () => {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
    let brandTitle = 'Campaign';
    try { brandTitle = JSON.parse(localStorage.getItem('branding') || '{}').title || brandTitle; } catch {}
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) { alert('Please allow pop-ups to download the report.'); return; }
    const RC = { 'Strong Support': '#10b981', 'Leaning Support': '#34d399', 'Undecided': '#f59e0b', 'Leaning Against': '#f97316', 'Strong Against': '#ef4444' };
    const half = Math.ceil(total / 2);
    const reached = totalSupport >= half;
    const w = (v, base) => Math.min(Math.max(rp(v, base), v > 0 ? 0.5 : 0), 100);
    const row = (label, v, base, color) =>
      `<tr><td class="lbl">${esc(label)}</td><td class="bar"><div style="width:${w(v, base)}%;background:${color}"></div></td><td class="val">${v} <span class="muted">(${fmtPct(rp(v, base))})</span></td></tr>`;
    const supRows  = SUPPORT_LEVELS.map(l => row(l, supportCounts[l] || 0, total, RC[l])).join('');
    const pipeRows = [['Total Voters', total, '#3b82f6'], ['Reached', votersContacted, '#06b6d4'], ['Committed', commitmentYes, '#10b981']]
      .map(([l, v, c]) => row(l, v, total, c)).join('');
    const islRows  = Object.entries(islandCounts).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([name, c]) => row(name || 'Unknown', c, total, '#3b82f6')).join('');
    const kpi = (label, val) => `<div class="kpi"><div class="kn">${val}</div><div class="kl">${esc(label)}</div></div>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(brandTitle)} — Analytics Report</title>
<style>
  *{box-sizing:border-box} body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;margin:0;padding:40px;background:#fff}
  .doc{max-width:760px;margin:0 auto}
  .top{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #0f172a;padding-bottom:14px;margin-bottom:24px}
  h1{font-size:24px;margin:0} .sub{font-size:12px;color:#64748b;margin-top:4px}
  h2{font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#475569;margin:28px 0 12px}
  .ptv{background:#f1f5f9;border-radius:12px;padding:18px 20px;margin-bottom:8px}
  .ptv .big{font-size:32px;font-weight:700;color:${reached ? '#10b981' : '#2563eb'}}
  .ptv .meta{font-size:12px;color:#64748b;margin-top:2px}
  .track{height:18px;background:#e2e8f0;border-radius:9px;overflow:hidden;margin-top:12px}
  .track>div{height:100%;border-radius:9px;background:${reached ? '#10b981' : '#2563eb'};width:${Math.min(rp(totalSupport, half), 100)}%}
  table{width:100%;border-collapse:collapse} td{padding:6px 0;vertical-align:middle}
  td.lbl{width:34%;font-size:13px;font-weight:600} td.bar{width:46%;padding:6px 12px}
  td.bar>div{height:12px;border-radius:6px;min-width:1px} td.val{width:20%;text-align:right;font-size:13px;white-space:nowrap}
  .muted{color:#94a3b8;font-weight:400}
  .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:6px}
  .kpi{background:#f1f5f9;border-radius:10px;padding:14px} .kn{font-size:22px;font-weight:700;color:#0f172a} .kl{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;margin-top:4px}
  .actions{margin-top:34px;text-align:center} button{font:inherit;font-size:13px;font-weight:600;padding:9px 22px;border-radius:8px;border:none;background:#2563eb;color:#fff;cursor:pointer;margin:0 6px}
  button.alt{background:#e2e8f0;color:#0f172a}
  @media print{.actions{display:none}body{padding:0}}
</style></head><body><div class="doc">
  <div class="top"><div><h1>${esc(brandTitle)}</h1><div class="sub">Campaign Analytics Report</div></div>
    <div class="sub" style="text-align:right">Generated ${new Date().toLocaleString()}<br>${total} voters</div></div>

  <h2>Path to Victory</h2>
  <div class="ptv"><div class="big">${fmtPct(rp(totalSupport, total))}</div>
    <div class="meta">${totalSupport} supporters of ${total} — ${half} needed to win ${reached ? '· target reached ✓' : `(${Math.max(half - totalSupport, 0)} to go)`}</div>
    <div class="track"><div></div></div></div>

  <h2>Support Breakdown</h2><table>${supRows}</table>

  <h2>Voter Pipeline</h2><table>${pipeRows}</table>

  <h2>Outreach Summary</h2>
  <div class="kpis">${kpi('Reach attempts', outreachTotal)}${kpi('Voters reached', votersContacted)}${kpi('Contact rate', fmtPct(rp(votersContacted, total)))}${kpi('Commitments', commitmentYes)}</div>

  <h2>Top Islands</h2><table>${islRows}</table>

  <div class="actions"><button onclick="window.print()">Print / Save as PDF</button><button class="alt" onclick="window.close()">Close</button></div>
</div></body></html>`;
    win.document.write(html);
    win.document.close();
  };

  // Donut + legend body (matches the Home/Overview support donut). Legend rows act on click
  // via a per-segment `onClick`, a per-segment `drill` key, or a shared `drillPrefix` + label.
  const renderDonutBody = ({ segments, centerVal, centerLabel, drillPrefix, legendAll }) => {
    const shown = segments.filter(s => s.value > 0);
    const sum = segments.reduce((a, s) => a + s.value, 0);
    if (sum === 0) return <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '30px 0', fontSize: 13 }}>No data yet</div>;
    // legendAll keeps every category in the legend (even 0-count ones); the donut still only draws non-zero arcs.
    const legend = legendAll ? segments : shown;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <SegmentDonut segments={shown} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-strong)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{centerVal}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8.5, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginTop: 3 }}>{centerLabel}</div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {legend.map(s => {
            const key = s.drill || (drillPrefix ? `${drillPrefix}${s.label}` : null);
            const act = s.onClick || (key ? () => setDrillDown(drillDown === key ? null : key) : null);
            return (
              <div key={s.label} onClick={act || undefined}
                title={s.hint || undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: act ? 'pointer' : 'default', borderRadius: 5, padding: '1px 2px' }}
                onMouseEnter={e => { if (act) e.currentTarget.style.opacity = '0.7'; }} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                <span style={{ color: 'var(--text-2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display || s.label}{act && <span style={{ color: 'var(--text-4)' }}> →</span>}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
                <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', width: 40, textAlign: 'right' }}>{fmtPct(rp(s.value, sum))}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Donut wrapped in a titled card.
  const renderDonut = ({ title, iconName, iconColor, note, ...body }) => (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: iconColor + '1f', color: iconColor }}><Icon name={iconName} size={16} /></span>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{title}</h3>
        </div>
        {note && <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>{note}</span>}
      </div>
      {renderDonutBody(body)}
    </div>
  );

  const isMobile = window.innerWidth < 768;

  // ── Shared drill-down panel (rendered in every tab) ──
  const renderDrillDown = () => {
    if (!drillDown) return null;
    const SUPPORTERS = ['Strong Support', 'Leaning Support'];
    const AGAINST    = ['Strong Against', 'Leaning Against'];
    const contactedIdSet = new Set(outreach.map(o => o.voter_id));

    let list = [], title = '', detailPath = '/search', detailState = null, detailLabel = 'Search';
    let kind = 'voters';

    // String-key drill-downs (Overview cards)
    if (drillDown === 'all')              { list = allVoters; title = 'All Voters'; }
    else if (drillDown === 'supporters')  { list = allVoters.filter(v => SUPPORTERS.includes(v.support)); title = 'Supporters'; detailState = { support: 'Supporters' }; }
    else if (drillDown === 'undecided')   { list = allVoters.filter(v => !v.support || v.support === 'Undecided'); title = 'Undecided Voters'; detailState = { support: 'Undecided' }; }
    else if (drillDown === 'against')     { list = allVoters.filter(v => AGAINST.includes(v.support)); title = 'Against Voters'; detailState = { support: 'Against' }; }
    else if (drillDown === 'committed')   { list = allVoters.filter(v => committedIds.has(v.id)); title = 'Committed Voters'; detailPath = '/gotv'; detailState = { filter: 'Priority' }; detailLabel = 'GOTV'; }
    else if (drillDown === 'contacted')   { list = allVoters.filter(v => contactedIdSet.has(v.id)); title = 'Contacted Voters'; detailPath = '/outreach'; detailState = { from: 'analytics' }; detailLabel = 'Outreach'; }
    else if (drillDown === 'events')              { list = upcomingEvents;    title = 'Upcoming Events';    detailPath = '/events';    detailLabel = 'Events'; kind = 'events'; }
    else if (drillDown === 'trips')               { list = upcomingTrips;     title = 'Upcoming Trips';     detailPath = '/trips';     detailLabel = 'Trips'; kind = 'trips'; }
    else if (drillDown === 'reminders-today')     { list = todayReminders;    title = "Today's Reminders";  detailPath = '/reminders'; detailLabel = 'Reminders'; kind = 'reminders'; }
    else if (drillDown === 'reminders-upcoming')  { list = upcomingReminders; title = 'Upcoming Reminders'; detailPath = '/reminders'; detailLabel = 'Reminders'; kind = 'reminders'; }
    // Dynamic prefixed drill-downs
    else if (drillDown.startsWith('support:'))  { const level = drillDown.slice('support:'.length);  list = allVoters.filter(v => v.support === level);                        title = `${level} Voters`;  detailState = { support: level }; }
    else if (drillDown.startsWith('gender:'))   { const g = drillDown.slice('gender:'.length);       list = allVoters.filter(v => (v.gender || 'Unknown') === g);              title = `${g} Voters`;       detailState = { gender: g === 'Unknown' ? '' : g }; }
    else if (drillDown.startsWith('age:'))      { const lbl = drillDown.slice('age:'.length); const band = AGE_BANDS.find(b => b.label === lbl); list = allVoters.filter(v => { const a = voterAge(v); if (lbl === 'Unknown') return a == null || isNaN(a); return band && a != null && !isNaN(a) && a >= band.min && a <= band.max; }); title = `Voters aged ${lbl}`; }
    else if (drillDown.startsWith('island:'))   { const i = drillDown.slice('island:'.length);       list = allVoters.filter(v => v.island === i);                            title = `Voters in ${i}`;    detailState = { island: i }; }
    else if (drillDown.startsWith('ward:'))     { const [, isl, w] = drillDown.split(':');           list = allVoters.filter(v => v.island === isl && v.ward === w);           title = `Voters in ${isl} · ${w}`; detailState = { island: isl, ward: w }; }

    const LIMIT = 50;
    const visible = list.slice(0, LIMIT);
    const remaining = list.length - visible.length;

    const renderVoterRow = (v) => (
      <div key={v.id} onClick={() => navigate(`/voter/${v.id}`)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-2)'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>
            {v.island}{v.ward ? ` · ${v.ward}` : ''}{v.house_name ? ` · ${v.house_name}` : ''}
          </div>
        </div>
        {v.support && (
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: (SUPPORT_COLORS[v.support]?.bg || 'var(--border)'), color: (SUPPORT_COLORS[v.support]?.text || 'var(--text-2)'), whiteSpace: 'nowrap' }}>
            {v.support}
          </span>
        )}
      </div>
    );
    const renderEventRow = (e) => (
      <div key={e.id} onClick={() => navigate('/events')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, cursor: 'pointer' }}
        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--hover)'}
        onMouseLeave={ev => ev.currentTarget.style.background = 'var(--surface-2)'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>
            📅 {e.date}{e.time ? ` · ${e.time}` : ''}{e.island ? ` · ${e.island}` : ''}{e.location ? ` · ${e.location}` : ''}
          </div>
        </div>
        {e.type && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#3b82f620', color: '#3b82f6', whiteSpace: 'nowrap' }}>{e.type}</span>}
      </div>
    );
    const renderTripRow = (t) => (
      <div key={t.id} onClick={() => navigate('/trips')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, cursor: 'pointer' }}
        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--hover)'}
        onMouseLeave={ev => ev.currentTarget.style.background = 'var(--surface-2)'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>
            📅 {t.start_date}{t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ''}{t.atoll ? ` · ${t.atoll}` : ''}
          </div>
        </div>
        {t.cost > 0 && <span style={{ fontSize: 10, color: '#8b5cf6', whiteSpace: 'nowrap' }}>MVR {Number(t.cost).toLocaleString()}</span>}
      </div>
    );
    const renderReminderRow = (r) => (
      <div key={r.id} onClick={() => navigate('/reminders')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, cursor: 'pointer' }}
        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--hover)'}
        onMouseLeave={ev => ev.currentTarget.style.background = 'var(--surface-2)'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || r.note || '(no title)'}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>
            ⏰ {r.reminder_date}{r.reminder_time ? ` · ${r.reminder_time}` : ''}{r.priority ? ` · ${r.priority}` : ''}
          </div>
        </div>
      </div>
    );
    const renderRow = (item) => {
      if (kind === 'events') return renderEventRow(item);
      if (kind === 'trips') return renderTripRow(item);
      if (kind === 'reminders') return renderReminderRow(item);
      return renderVoterRow(item);
    };

    return (
      <div ref={drillDownRef} style={{ ...card, padding: '16px', marginTop: 20, scrollMarginTop: 80 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>
            {title} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({list.length})</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate(detailPath, detailState ? { state: detailState } : undefined)}
              style={{ fontSize: 11, color: 'var(--link)', background: 'none', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
              View all in {detailLabel} →
            </button>
            <button onClick={() => setDrillDown(null)}
              style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>
          </div>
        </div>
        {list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-4)', fontSize: 13 }}>Nothing to show</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {visible.map(renderRow)}
            {remaining > 0 && (
              <div style={{ textAlign: 'center', padding: '8px', fontSize: 12, color: 'var(--text-3)' }}>
                + {remaining} more — <button onClick={() => navigate(detailPath, detailState ? { state: detailState } : undefined)} style={{ background: 'none', border: 'none', color: 'var(--link)', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 12 }}>view all</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>

      {/* Editorial header + tabs (un-boxed — matches Home dashboard) */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 6 }}>Insights</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? 26 : 30, fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1.1 }}>Campaign Analytics</h2>
          </div>
          <button onClick={downloadReport} title="Open a printable report"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0, background: 'transparent', border: '1px solid var(--border)', borderRadius: 9, padding: isMobile ? '8px 12px' : '9px 16px', color: 'var(--text-2)', fontSize: isMobile ? 12 : 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.55)'; e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(59,130,246,0.10)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.background = 'transparent'; }}>
            <Icon name="download" size={15} />{isMobile ? 'Report' : 'Download Report'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {TABS.map(t => {
            const on = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)}
                aria-current={on ? 'true' : undefined}
                style={{
                  padding: isMobile ? '8px 14px' : '9px 20px', borderRadius: '9px',
                  border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                  background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                  color: on ? '#60a5fa' : 'var(--text-2)',
                  fontSize: isMobile ? '12px' : '13px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace",
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s'
                }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{t}</button>
            );
          })}
        </div>
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────── */}
      {tab === 'Overview' && (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          {/* Hero: Victory ring + Support donut (matches Home dashboard) */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: 16 }}>
            {/* Victory ring */}
            <div style={{ ...card, padding: 24, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 26 }}>
              <ProgressRing pct={needed > 0 ? (totalSupport / needed) * 100 : 0} color={toWin <= 0 ? '#10b981' : '#3b82f6'}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 700, color: 'var(--text-strong)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtPct(needed > 0 ? Math.min((totalSupport / needed) * 100, 100) : 0)}
                </div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 5 }}>To Victory</div>
              </ProgressRing>
              <div style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6' }}><Icon name="target" size={16} /></span>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Path to Victory</h3>
                </div>
                {[
                  { label: 'Your supporters', value: totalSupport, color: '#10b981', key: 'supporters' },
                  { label: 'Needed to win',   value: needed,       color: '#3b82f6', key: null },
                  { label: toWin > 0 ? 'Votes to go' : 'Secured by', value: toWin > 0 ? toWin : Math.abs(toWin), color: toWin > 0 ? '#f59e0b' : '#10b981', key: null },
                ].map(row => (
                  <div key={row.label} onClick={() => row.key && setDrillDown(drillDown === row.key ? null : row.key)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-2)', cursor: row.key ? 'pointer' : 'default' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: row.color, flexShrink: 0 }} />{row.label}
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)', fontVariantNumeric: 'tabular-nums' }}>{row.value}{row.key && <span style={{ color: 'var(--text-4)', fontWeight: 400, fontSize: 13 }}> →</span>}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Support breakdown — toggles between support levels and competitor vote-share */}
            <div style={{ ...card, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#8b5cf61f', color: '#8b5cf6' }}><Icon name="pie" size={16} /></span>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Support Breakdown</h3>
                </div>
                {!editingOpponents && supportView === 'competitor' && (
                  <button onClick={openOpponentEditor} style={{ background: 'var(--border)', border: 'none', color: 'var(--text-2)', fontSize: 12, padding: '5px 10px', borderRadius: 6, cursor: 'pointer' }}>Manage Opponents</button>
                )}
              </div>
              {!editingOpponents && (
                <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 2, marginBottom: 16 }}>
                  {[{ k: 'levels', t: 'By support level' }, { k: 'competitor', t: 'By competitor' }].map(v => {
                    const on = supportView === v.k;
                    return (
                      <button key={v.k} onClick={() => setSupportView(v.k)}
                        style={{ padding: '5px 11px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 600, background: on ? 'rgba(59,130,246,0.16)' : 'transparent', color: on ? '#60a5fa' : 'var(--text-3)', transition: 'background 0.15s, color 0.15s' }}>
                        {v.t}
                      </button>
                    );
                  })}
                </div>
              )}
              {editingOpponents ? (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>Add opponent names. Vote counts are pulled automatically from voter profiles.</div>
                  {opponentDraft.map((o, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input value={o.name} onChange={e => { const d = [...opponentDraft]; d[i].name = e.target.value; setOpponentDraft(d); }}
                        placeholder="Opponent name"
                        style={{ flex: 1, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', color: 'var(--text)', fontSize: 13 }} />
                      <button onClick={() => setOpponentDraft(opponentDraft.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setOpponentDraft([...opponentDraft, { name: '' }])}
                    style={{ background: 'var(--border)', border: 'none', color: 'var(--link)', fontSize: 12, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', marginTop: 4 }}>
                    + Add Opponent
                  </button>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={saveOpponents}
                      style={{ background: '#3b82f6', border: 'none', color: 'white', fontSize: 13, padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Save</button>
                    <button onClick={() => setEditingOpponents(false)}
                      style={{ background: 'var(--border)', border: 'none', color: 'var(--text-2)', fontSize: 13, padding: '7px 16px', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : supportView === 'competitor' ? (
                renderDonutBody({
                  centerVal: total, centerLabel: 'Voters',
                  segments: [
                    { label: 'You', value: totalSupport, color: '#10b981', drill: 'supporters' },
                    ...(opponentStats.length > 0
                      ? opponentStats.map((o, i) => ({ label: o.name, value: o.votes, color: ['#ef4444', '#f97316', '#ec4899', '#a855f7', '#dc2626'][i % 5] }))
                      : [{ label: 'Against', value: totalAgainst, color: '#ef4444', drill: 'against' }]),
                    ...(opponentStats.length > 0 && unassignedAgainst > 0 ? [{ label: 'Other / Unassigned', value: unassignedAgainst, color: '#7f1d1d' }] : []),
                    { label: 'Undecided', value: undecided, color: '#f59e0b', drill: 'undecided' },
                  ],
                })
              ) : (
                renderDonutBody({
                  centerVal: total, centerLabel: 'Voters', legendAll: true,
                  segments: supportSegments.map(s =>
                    (s.label === 'Leaning Against' || s.label === 'Strong Against')
                      ? { ...s, onClick: () => setSupportView('competitor'), hint: 'See how the against bloc splits by opponent' }
                      : { ...s, drill: `support:${s.label}` }
                  ),
                })
              )}
              {!editingOpponents && (
                <div onClick={() => setDrillDown(drillDown === 'committed' ? null : 'committed')}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.7'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: '#8b5cf6', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-2)', flex: 1, minWidth: 0 }}>Committed <span style={{ color: 'var(--text-4)' }}>· of {totalSupport} supporters →</span></span>
                  <span style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{commitmentYes}</span>
                  <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', width: 40, textAlign: 'right' }}>{fmtPct(rp(commitmentYes, totalSupport))}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '14px' }}>
            {[
              { key: 'events',             label: 'Upcoming Events',    value: eventCounts.upcoming,    sub: `${eventCounts.total} total`,        color: '#f59e0b', icon: 'calendar' },
              { key: 'reminders-today',    label: 'Reminders Today',    value: reminderCounts.today,    sub: null,                                color: '#f59e0b', icon: 'clock' },
              { key: 'reminders-upcoming', label: 'Upcoming Reminders', value: reminderCounts.upcoming, sub: null,                                color: '#3b82f6', icon: 'bell' },
              { key: 'trips',              label: 'Upcoming Trips',     value: upcomingTripsCount,      sub: null,                                color: '#10b981', icon: 'plane' },
            ].map(m => {
              const active = m.key && drillDown === m.key;
              const handleClick = () => {
                if (m.nav) { navigate(m.nav); return; }
                if (m.key) setDrillDown(active ? null : m.key);
              };
              return (
                <div key={m.label} onClick={handleClick}
                  style={{ ...card, padding: '14px 16px', cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease', borderColor: active ? m.color : 'var(--border)', boxShadow: active ? `0 0 0 1px ${m.color}, var(--shadow-md)` : 'var(--shadow-sm)', minHeight: 96, position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = active ? `0 0 0 1px ${m.color}, var(--shadow-lg)` : 'var(--shadow-md)'; e.currentTarget.style.borderColor = m.color; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = active ? `0 0 0 1px ${m.color}, var(--shadow-md)` : 'var(--shadow-sm)'; e.currentTarget.style.borderColor = active ? m.color : 'var(--border)'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{m.label}</span>
                    <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: m.color + '1f', color: m.color, flexShrink: 0 }}>
                      <Icon name={m.icon} size={15} />
                    </span>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: m.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{m.value}</div>
                  {m.sub && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '6px' }}>{m.sub}</div>}
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: m.color, opacity: active ? 1 : 0, transition: 'opacity 0.15s' }} />
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* ── OUTREACH ─────────────────────────────────────── */}
      {tab === 'Outreach' && (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '14px' }}>
            {[
              { label: 'Reach Attempts', value: outreachTotal,                         color: '#3b82f6', key: 'contacted', icon: 'phone' },
              { label: 'Voters Reached', value: votersContacted,                       color: '#10b981', key: 'contacted', icon: 'users' },
              { label: 'Contact Rate',   value: fmtPct(rp(votersContacted, total)),    color: '#8b5cf6', key: null,        icon: 'trending' },
              { label: 'Commitments',    value: commitmentYes,                          color: '#f59e0b', key: 'committed', icon: 'target' },
            ].map(m => {
              const active = m.key && drillDown === m.key;
              return (
                <div key={m.label} onClick={() => m.key && setDrillDown(active ? null : m.key)}
                  style={{ ...card, padding: '14px 16px', cursor: m.key ? 'pointer' : 'default', transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease', borderColor: active ? m.color : 'var(--border)', boxShadow: active ? `0 0 0 1px ${m.color}, var(--shadow-md)` : 'var(--shadow-sm)', minHeight: 96, position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { if (!m.key) return; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = active ? `0 0 0 1px ${m.color}, var(--shadow-lg)` : 'var(--shadow-md)'; e.currentTarget.style.borderColor = m.color; }}
                  onMouseLeave={e => { if (!m.key) return; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = active ? `0 0 0 1px ${m.color}, var(--shadow-md)` : 'var(--shadow-sm)'; e.currentTarget.style.borderColor = active ? m.color : 'var(--border)'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{m.label}</span>
                    <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: m.color + '1f', color: m.color, flexShrink: 0 }}>
                      <Icon name={m.icon} size={15} />
                    </span>
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: m.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{m.value}</div>
                  {m.key && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: m.color, opacity: active ? 1 : 0, transition: 'opacity 0.15s' }} />}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {(() => {
              const palette = ['#3b82f6', '#06b6d4', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b'];
              let pi = 0;
              const segs = OUTREACH_OUTCOMES.filter(o => (outcomeCounts[o] || 0) > 0).map(o => {
                const color = o === 'Commitment Made' ? '#10b981'
                  : (o.includes('Hostile') || o.includes('Refused') || o.includes('Withdrawn')) ? '#ef4444'
                  : palette[pi++ % palette.length];
                return { label: o, value: outcomeCounts[o] || 0, color };
              });
              return renderDonut({ title: 'Outcomes', iconName: 'target', iconColor: '#10b981', centerVal: segs.reduce((s, x) => s + x.value, 0), centerLabel: 'Records', segments: segs });
            })()}
            {(() => {
              const palette = ['#06b6d4', '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#64748b'];
              const segs = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([type, c], i) => ({ label: type, value: c, color: palette[i % palette.length] }));
              return renderDonut({ title: 'Contact Methods', iconName: 'phone', iconColor: '#06b6d4', centerVal: segs.reduce((s, x) => s + x.value, 0), centerLabel: 'Contacts', segments: segs });
            })()}
          </div>

          <div style={card}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px', color: 'var(--text)' }}>Team Activity</h3>
            {teamStats.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '30px 0', fontSize: '13px' }}>No outreach recorded yet</div>
            ) : teamStats.map(([name, c], i) => {
              const colors = ['#f59e0b', 'var(--text-2)', '#cd7c32', '#3b82f6', '#10b981', '#8b5cf6'];
              return (
                <div key={name} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                    <span style={{ color: 'var(--text)' }}>{i + 1}. {name}</span>
                    <span style={{ color: 'var(--text-2)' }}>{c} ({fmtPct(rp(c, outreachTotal))})</span>
                  </div>
                  <Bar n={c} base={outreachTotal} color={colors[i] || '#3b82f6'} height={14} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── GEOGRAPHY ────────────────────────────────────── */}
      {tab === 'Geography' && (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          <div style={card}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px', color: 'var(--text)' }}>Island & Ward Distribution</h3>
            {Object.entries(islandCounts).sort((a,b) => b[1]-a[1]).map(([island, c]) => {
              const isExpanded = expandedIslands.has(island);
              const islandVoters = allVoters.filter(v => (v.island || 'Unknown') === island);
              const islandWardCounts = countBy(islandVoters, 'ward');
              return (
                <div key={island} style={{ marginBottom: '12px' }}>
                  <div style={{ cursor: 'pointer' }} onClick={() => toggleIsland(island)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>
                        <span style={{ marginRight: '8px', color: 'var(--text-3)', fontSize: '11px' }}>{isExpanded ? '▼' : '▶'}</span>
                        {island || 'Unknown'}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>{c} ({fmtPct(rp(c, total))})</span>
                        <span style={{ fontSize: '11px', color: '#3b82f6', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onClick={e => { e.stopPropagation(); setDrillDown(`island:${island}`); }}>
                          View →
                        </span>
                      </div>
                    </div>
                    <div style={{ height: '14px', background: 'var(--surface-2)', borderRadius: '7px', border: '1px solid var(--border)' }}>
                      <div style={{ height: '100%', width: `${c > 0 ? Math.max(rp(c, total), 0.4) : 0}%`, background: '#3b82f6', borderRadius: '7px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: '8px', paddingLeft: '20px', borderLeft: '2px solid var(--border)' }}>
                      {Object.entries(islandWardCounts).sort((a,b) => b[1]-a[1]).map(([ward, wc]) => (
                        <div key={ward} style={{ marginBottom: '8px', cursor: 'pointer' }}
                          onClick={() => setDrillDown(`ward:${island}:${ward}`)}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text)' }}>{ward || 'No Ward'}</span>
                            <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>{wc} ({fmtPct(rp(wc, c))}) →</span>
                          </div>
                          <div style={{ height: '12px', background: 'var(--surface-2)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                            <div style={{ height: '100%', width: `${wc > 0 ? Math.max(rp(wc, c), 0.4) : 0}%`, background: '#10b981', borderRadius: '6px', transition: 'width 0.5s' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={card}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '14px', color: 'var(--text)' }}>Support by Island</h3>
            {Object.keys(islandCounts).map(island => {
              const iv = allVoters.filter(v => (v.island || 'Unknown') === island);
              const is = iv.filter(v => v.support === 'Strong Support' || v.support === 'Leaning Support').length;
              const it = iv.length;
              const ip = rp(is, it);
              return (
                <div key={island} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)' }}>{island}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-2)', flexShrink: 0 }}>{is}/{it} ({fmtPct(ip)})</span>
                  </div>
                  <div style={{ height: '14px', background: 'var(--surface-2)', borderRadius: '7px', border: '1px solid var(--border)' }}>
                    <div style={{ height: '100%', width: `${is > 0 ? Math.max(ip, 0.4) : 0}%`, background: ip >= 50 ? '#10b981' : '#ef4444', borderRadius: '7px', transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── DEMOGRAPHICS ─────────────────────────────────── */}
      {tab === 'Demographics' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginTop: '20px' }}>
          {renderDonut({
            title: 'Gender', iconName: 'users', iconColor: '#3b82f6',
            centerVal: total, centerLabel: 'Voters', drillPrefix: 'gender:',
            segments: Object.entries(genderCounts).sort((a, b) => b[1] - a[1]).map(([g, c]) => ({
              label: g, value: c,
              color: g === 'Male' ? '#3b82f6' : g === 'Female' ? '#ec4899' : g === 'Other' ? '#f59e0b' : 'var(--text-3)',
            })),
          })}
          {renderDonut({
            title: 'Age', iconName: 'clock', iconColor: '#8b5cf6',
            centerVal: total, centerLabel: 'Voters', drillPrefix: 'age:',
            note: ageKnown < total ? `${fmtPct(rp(ageKnown, total))} have age data` : null,
            segments: [
              ...AGE_BANDS.map((b, i) => ({ label: b.label, value: ageCounts[b.label] || 0, color: ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc'][i % 4] })),
              { label: 'Unknown', display: 'No age data', value: ageCounts['Unknown'] || 0, color: 'var(--text-3)' },
            ],
          })}
        </div>
      )}

      {/* ── TRENDS ───────────────────────────────────────── */}
      {tab === 'Trends' && (
        <div style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Support Momentum</h3>
              <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: netTotal > 0 ? '#10b981' : netTotal < 0 ? '#ef4444' : 'var(--text-3)' }}>
                {netTotal > 0 ? '+' : ''}{netTotal} <span style={{ fontWeight: 400, color: 'var(--text-3)', fontSize: 11 }}>net supporters · 8 wks</span>
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>Net supporters gained (green) or lost (red) each week, from the support-change log.</div>
            {(() => {
              const netMax = Math.max(1, ...netByWeek.map(v => Math.abs(v)));
              const H = 150, HALF = H / 2;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, height: H }}>
                    {weeks.map((d, i) => {
                      const v = netByWeek[i];
                      const bh = v === 0 ? 0 : Math.max(2, Math.round(Math.abs(v) / netMax * (HALF - 8)));
                      return (
                        <div key={i} title={`Week of ${weekLabel(d)}: ${v > 0 ? '+' : ''}${v}`} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                            {v > 0 && <div style={{ width: '60%', maxWidth: 28, height: bh, background: '#10b981', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />}
                          </div>
                          <div style={{ height: 1, background: 'var(--border)' }} />
                          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                            {v < 0 && <div style={{ width: '60%', maxWidth: 28, height: bh, background: '#ef4444', borderRadius: '0 0 4px 4px', transition: 'height 0.5s ease' }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {weeks.map((d, i) => (
                      <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 9.5, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>{weekLabel(d)}</div>
                        <div style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: netByWeek[i] > 0 ? '#10b981' : netByWeek[i] < 0 ? '#ef4444' : 'var(--text-4)' }}>
                          {netByWeek[i] > 0 ? '+' : ''}{netByWeek[i]}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Outreach Volume</h3>
              <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#3b82f6' }}>
                {volTotal} <span style={{ fontWeight: 400, color: 'var(--text-3)', fontSize: 11 }}>contacts · 8 wks</span>
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>Outreach events logged each week.</div>
            {(() => {
              const volMax = Math.max(1, ...volByWeek);
              const VH = 140;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: VH }}>
                    {weeks.map((d, i) => {
                      const v = volByWeek[i];
                      const bh = v === 0 ? 0 : Math.max(2, Math.round(v / volMax * (VH - 22)));
                      return (
                        <div key={i} title={`Week of ${weekLabel(d)}: ${v}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 3, fontVariantNumeric: 'tabular-nums', minHeight: 14 }}>{v || ''}</div>
                          <div style={{ width: '60%', maxWidth: 30, height: bh, background: '#3b82f6', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {weeks.map((d, i) => (
                      <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>{weekLabel(d)}</div>
                    ))}
                  </div>
                </>
              );
            })()}
            {volTotal === 0 && netTotal === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-4)', fontSize: 12, marginTop: 14 }}>No activity logged in the last 8 weeks yet.</div>
            )}
          </div>
        </div>
      )}

      {/* Shared drill-down panel — visible across all tabs */}
      {renderDrillDown()}
    </div>
  );
}
